import { useTranslation } from 'react-i18next'

import { formatAmount } from '@/lib/money'

/** What a further individual costs on a subscription that already exists. */
export type AddSeatPreview = {
  amountDue: number | null
  currency: string
  interval: 'month' | 'year'
  recurringAmount: number
  renewsAt?: string
}

/**
 * What joining the existing subscription costs, and the confirmation for it.
 *
 * Laid out the same way as a plan card in the picker — frequency, then the cost (the
 * thing that should stand out), then the fine print — so the two variants of this dialog
 * read as one design rather than two different ones stitched together. The one thing
 * unique to this path is what gets charged right now, which is why it gets its own line.
 *
 * The amount can be absent — the proration is priced by Stripe, and a failed preview is
 * not a reason to block the purchase. The wording then drops the figure rather than
 * substituting a guess.
 */
export const AddSeatSummary = ({
  addSeat,
  locale,
  renewal,
}: {
  addSeat: AddSeatPreview
  locale: string
  renewal?: string
}) => {
  const { t } = useTranslation()
  const recurringAmount = formatAmount(addSeat.recurringAmount, addSeat.currency, locale)

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-xl border p-[18px_20px]">
        <span className="block font-heading text-[17px] font-bold text-brand-gran">
          {t(addSeat.interval === 'year' ? 'subscribeDialog.plan.year' : 'subscribeDialog.plan.month')}
        </span>
        <span className="mt-1 block font-heading text-[24px] font-black text-brand-gran">
          {recurringAmount}
          <span className="ml-1 font-body text-[15px] font-semibold text-[#586574]">
            {t(addSeat.interval === 'year' ? 'subscribeDialog.plan.perYear' : 'subscribeDialog.plan.perMonth')}
          </span>
        </span>
        <span className="mt-2.5 block text-[15px] text-[#586574]">
          {addSeat.amountDue === null
            ? t('subscribeDialog.addSeat.dueUnknown')
            : renewal
              ? t('subscribeDialog.addSeat.dueNowAndNext', {
                  amount: recurringAmount,
                  date: renewal,
                  dueAmount: formatAmount(addSeat.amountDue, addSeat.currency, locale),
                })
              : t('subscribeDialog.addSeat.dueNow', {
                  amount: formatAmount(addSeat.amountDue, addSeat.currency, locale),
                })}
        </span>
      </div>
      <p className="text-xs text-[#586574]">{t('subscribeDialog.taxNote')}</p>
      <p className="text-xs text-[#586574]">{t('subscribeDialog.addSeat.changePeriod')}</p>
    </div>
  )
}
